import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CurrencyDollar, CaretDown, CheckCircle } from '@phosphor-icons/react';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getLinksForCostGuide } from '../data/internalLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n).toLocaleString()}`;

export default function CostGuideDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setGuide(null);
    setError(false);
    axios.get(`${API_URL}/api/guides/${slug}`)
      .then((r) => {
        const g = r.data.guide;
        setGuide(g);
        setPageMeta({
          title: `${g.title} | ConstructHelp`,
          description: g.summary,
          ogType: 'article',
          url: `${window.location.origin}/guides/${g.slug}`,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: (g.faqs || []).map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
            breadcrumbLd([
              { name: 'Home', url: window.location.origin },
              { name: 'Cost Guides', url: `${window.location.origin}/guides` },
              { name: g.title },
            ]),
          ],
        });
        window.scrollTo(0, 0);
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Guide not found</p>
          <Link to="/guides" className="text-[#D95800] font-bold underline">Back to all guides</Link>
        </div>
      </div>
    );
  }
  if (!guide) {
    return <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center"><p className="font-bold text-neutral-500">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="cost-guide-detail">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link to="/guides" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white text-xs font-bold uppercase tracking-wider mb-4 transition-colors" data-testid="back-to-guides-link">
            <ArrowLeft size={14} weight="bold" /> All Cost Guides
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-black uppercase tracking-widest bg-[#2B4C6F] text-white px-2 py-0.5 border border-black">{guide.trade}</span>
            <span className="text-[10px] font-black uppercase tracking-widest bg-[#D95800] text-black px-2 py-0.5 border border-white">2026 Pricing</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight mb-4">{guide.title}</h1>
          <div className="inline-flex items-center gap-2 bg-white border-2 border-black shadow-[4px_4px_0px_#D95800] px-4 py-2">
            <CurrencyDollar size={20} weight="bold" className="text-emerald-600" />
            <span className="font-black text-neutral-900 text-lg" data-testid="guide-cost-range">
              {fmt(guide.cost_low)} – {fmt(guide.cost_high)}
            </span>
            <span className="text-xs text-neutral-500 font-bold uppercase">national avg</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed mb-10 border-l-4 border-[#D95800] pl-4">{guide.summary}</p>

        {/* Sections */}
        {(guide.sections || []).map((s, i) => (
          <div key={i} className="mb-8">
            <h2 className="text-lg md:text-xl font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-3">
              <span className="text-[#D95800] mr-2">{String(i + 1).padStart(2, '0')}</span>{s.heading}
            </h2>
            <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">{s.body}</p>
          </div>
        ))}

        {/* Cost table */}
        <div className="mb-10 bg-white dark:bg-[#0F172A] border-4 border-black shadow-[6px_6px_0px_#000]" data-testid="guide-cost-table">
          <div className="bg-[#0F172A] border-b-4 border-black px-4 py-3">
            <h2 className="text-white font-black uppercase text-sm tracking-wide">Itemized cost breakdown</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-neutral-200 dark:border-neutral-700 text-left">
                <th className="px-4 py-2 font-black uppercase text-xs text-neutral-500">Item</th>
                <th className="px-4 py-2 font-black uppercase text-xs text-neutral-500 text-right">Low</th>
                <th className="px-4 py-2 font-black uppercase text-xs text-neutral-500 text-right">High</th>
              </tr>
            </thead>
            <tbody>
              {(guide.cost_table || []).map((row, i) => (
                <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800">
                  <td className="px-4 py-2.5 font-medium text-neutral-800 dark:text-neutral-200">{row.item}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">{fmt(row.low)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">{fmt(row.high)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* FAQs */}
        <div className="mb-10">
          <h2 className="text-lg md:text-xl font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-4">Frequently asked questions</h2>
          {(guide.faqs || []).map((f, i) => (
            <details key={i} className="group bg-white dark:bg-[#0F172A] border-2 border-black mb-2 shadow-[3px_3px_0px_#000]">
              <summary className="cursor-pointer font-bold text-sm text-neutral-800 dark:text-neutral-200 flex items-center gap-2 px-4 py-3">
                <CaretDown size={14} weight="bold" className="text-[#D95800] group-open:rotate-180 transition-transform shrink-0" /> {f.q}
              </summary>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 px-4 pb-4 pl-10 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        {/* Internal links: hire trade + related articles */}
        {(() => {
          const links = getLinksForCostGuide(guide.slug);
          if (!links || (!links.trade && links.articles.length === 0)) return null;
          return (
            <div className="mb-10 space-y-3" data-testid="guide-internal-links">
              {links.trade && (
                <Link to={`/hire/${links.trade.slug}`}
                  className="block bg-neutral-900 border-2 border-black p-5 shadow-[4px_4px_0px_#D95800] hover:translate-x-0.5 transition-transform"
                  data-testid="guide-hire-trade-link">
                  <p className="text-xs font-black uppercase text-[#D95800] mb-1">Ready to hire?</p>
                  <p className="font-black text-white">Find vetted local {links.trade.name.toLowerCase()} and compare free quotes →</p>
                </Link>
              )}
              {links.articles.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {links.articles.map((a) => (
                    <Link key={a.slug} to={`/hiring-guides/${a.slug}`}
                      className="bg-white dark:bg-[#0F172A] border-2 border-black p-4 hover:bg-orange-50 shadow-[3px_3px_0px_#000]"
                      data-testid={`guide-article-${a.slug}`}>
                      <p className="font-black text-sm text-neutral-900 dark:text-white leading-snug mb-1">{a.h1}</p>
                      <span className="text-xs font-bold text-[#D95800]">Read the guide →</span>
                    </Link>
                  ))}
                </div>
              )}
              <Link to="/home" data-testid="guide-features-strip"
                className="block bg-white dark:bg-[#0F172A] border-2 border-black p-4 hover:bg-orange-50 shadow-[3px_3px_0px_#000]">
                <p className="text-xs font-black uppercase text-[#D95800] mb-0.5">Free tools for pros & homeowners</p>
                <p className="font-black text-sm text-neutral-900 dark:text-white">Voice translator, AI scheduling, escrow payments & more — see everything you get →</p>
              </Link>
            </div>
          );
        })()}

        {/* Conversion CTA */}
        <div className="bg-[#2B4C6F] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center" data-testid="guide-cta-block">
          <h2 className="text-xl md:text-2xl font-black uppercase text-white mb-2">What will YOUR project cost?</h2>
          <p className="text-white/80 mb-5 max-w-md mx-auto text-sm">
            Get free itemized quotes from verified local contractors. Compare side-by-side, pay by milestone with escrow protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/get-quotes')}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#D95800] text-black font-black uppercase text-sm tracking-wide border-2 border-white shadow-[4px_4px_0px_#fff] hover:-translate-y-0.5 transition-transform"
              data-testid="guide-get-quotes-cta"
            >
              Get Free Quotes <ArrowRight size={16} weight="bold" />
            </button>
            <button
              onClick={() => navigate('/browse-worker-teams')}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-transparent text-white font-black uppercase text-sm tracking-wide border-2 border-white hover:bg-white/10 transition-colors"
              data-testid="guide-browse-pros-cta"
            >
              <CheckCircle size={16} weight="bold" /> Browse Verified Pros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
